import React, { Component } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DateTime } from "luxon";
import MultiSelectModal from "./modals/MultiSelectModal";
import TripMembersModal from "./modals/TripMembersModal";
import { stableEventKey } from "../Common/GuideDiff";
import { computeBalancesByCurrency } from "../Models/ExpenseBalances";
import { slice1Actions } from "../store/reducer";
import "./ExpensesSection.css";

// Expense Tracking MVP (docs/competitive-analysis.md's "Expense tracking
// MVP scope"). Unlike CommentsSection/PollsSection, this is NOT a Firestore
// subcollection with instant writes — expenses/tripMembers are guide
// CONTENT (like Places/Categories), held in LOCAL state here and written
// back to window.guide_obj only on explicit Save, same pattern
// DestinationsScreen.js already uses for its own guide-content field
// (window.guide_obj.destinations = ...; dispatch(setGuide({guide:null}))).
// Self-connects to Redux for guide_meta/user_info/Authorization the same
// way PollsSection.js does, but reads/writes window.guide_obj directly
// rather than calling DataBase.
//
// Trip-member add/remove now lives in its own screen (TripMembersSection.js,
// "recorded like Destinations" per the user's request — a trip's roster is
// worth keeping on its own, independent of whether any expenses ever get
// logged). This screen only reads window.guide_obj.tripMembers for its
// pickers; `refreshTripMembers` re-reads it after TripMembersModal closes,
// since that modal owns the authoritative add/remove/save flow now.
class ExpensesSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tripMembers: (window.guide_obj?.tripMembers || []).slice(),
      expenses: (window.guide_obj?.expenses || []).slice(),
      editingExpense: null, // null, or a draft expense object while adding/editing
      editingExpenseIndex: -1, // -1 while adding, >=0 while editing an existing one
      dirty: false,
      saveMessage: "",
      pickerMode: null, // null | "category" | "link" | "paidBy" | "addSplitMember"
      showTripMembersModal: false,
    };
  }

  markDirty = () => {
    this.setState({ dirty: true, saveMessage: "" });
  };

  // ---- Trip members (managed in TripMembersModal; this just re-syncs) ----

  openTripMembersModal = () => this.setState({ showTripMembersModal: true });

  closeTripMembersModal = () => {
    this.setState({ showTripMembersModal: false, tripMembers: (window.guide_obj?.tripMembers || []).slice() });
  };

  // ---- Expense add/edit form ----

  startAddExpense = () => {
    const { tripMembers } = this.state;
    const equalPercent = tripMembers.length > 0 ? +(100 / tripMembers.length).toFixed(2) : 0;
    this.setState({
      editingExpense: {
        amount: "",
        currency: "USD",
        categoryId: "",
        placeId: null,
        eventKey: null,
        linkLabel: "",
        date: DateTime.utc().toISODate(),
        paidBy: tripMembers.length > 0 ? tripMembers[0].id : "",
        splits: tripMembers.map((m) => ({ memberId: m.id, percent: equalPercent })),
        title: "",
        note: "",
      },
      editingExpenseIndex: -1,
    });
  };

  startEditExpense = (expense, index) => {
    this.setState({ editingExpense: { ...expense, splits: (expense.splits || []).map((s) => ({ ...s })) }, editingExpenseIndex: index });
  };

  cancelEditExpense = () => {
    this.setState({ editingExpense: null, editingExpenseIndex: -1 });
  };

  updateDraft = (field, value) => {
    this.setState((prev) => ({ editingExpense: { ...prev.editingExpense, [field]: value } }));
  };

  updateSplitPercent = (memberId, percent) => {
    this.setState((prev) => ({
      editingExpense: {
        ...prev.editingExpense,
        splits: prev.editingExpense.splits.map((s) => (s.memberId === memberId ? { ...s, percent } : s)),
      },
    }));
  };

  splitTotal = () => {
    const { editingExpense } = this.state;
    if (!editingExpense) return 0;
    return editingExpense.splits.reduce((sum, s) => sum + (parseFloat(s.percent) || 0), 0);
  };

  saveExpenseDraft = () => {
    const { editingExpense, editingExpenseIndex, expenses } = this.state;
    const amount = parseFloat(editingExpense.amount);
    if (isNaN(amount) || amount <= 0) {
      alert("Enter a valid amount.");
      return;
    }
    if (!editingExpense.paidBy) {
      alert("Select who paid.");
      return;
    }
    const total = this.splitTotal();
    if (Math.abs(total - 100) > 0.5) {
      alert(`The split must total 100% — it currently totals ${total.toFixed(1)}%.`);
      return;
    }
    const cleaned = {
      ...editingExpense,
      amount,
      id: editingExpense.id || `exp_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
      splits: editingExpense.splits.filter((s) => (parseFloat(s.percent) || 0) > 0),
    };
    delete cleaned.linkLabel;
    const newExpenses = expenses.slice();
    if (editingExpenseIndex === -1) newExpenses.push(cleaned);
    else newExpenses[editingExpenseIndex] = cleaned;
    this.setState({ expenses: newExpenses, editingExpense: null, editingExpenseIndex: -1 }, this.markDirty);
  };

  deleteExpense = (index) => {
    this.setState(
      (prev) => ({ expenses: prev.expenses.filter((_, i) => i !== index) }),
      this.markDirty
    );
  };

  // ---- Pickers (category / place-or-event link / paid-by) ----

  openCategoryPicker = () => this.setState({ pickerMode: "category" });
  openLinkPicker = () => this.setState({ pickerMode: "link" });
  openPaidByPicker = () => this.setState({ pickerMode: "paidBy" });
  openAddSplitMemberPicker = () => this.setState({ pickerMode: "addSplitMember" });
  closePicker = () => this.setState({ pickerMode: null });

  categoryItems = () => {
    const categories = window.guide_obj?.categories || [];
    return categories.map((c) => ({ id: c.id, label: c.name, name: c.name, value: c.id }));
  };

  linkItems = () => {
    const places = (window.guide_obj?.places?.placesList || []).filter(
      (p) => !p.Hidden && p.PType !== "PolyLine" && p.PType !== "Polygon"
    );
    const events = window.guide_obj?.schedule?.events || [];
    const items = [{ id: "none", label: "No link", name: "No link", value: "none" }];
    places.forEach((p) => items.push({ id: `place:${p.ID}`, label: `Place: ${p.Name}`, name: p.Name, value: `place:${p.ID}` }));
    events.forEach((e) => {
      const key = e.CommentKey || stableEventKey(e);
      items.push({ id: `event:${key}`, label: `Event: ${e.Subject}`, name: e.Subject, value: `event:${key}` });
    });
    return items;
  };

  paidByItems = () => this.state.tripMembers.map((m) => ({ id: m.id, label: m.name, name: m.name, value: m.id }));

  // Trip members added to the guide AFTER this expense was first created
  // never made it into its splits array (splits is seeded once, from
  // whoever was in the roster at creation time) — this is how they get
  // added to an existing expense's split without recreating it.
  addSplitMemberItems = () => {
    const { editingExpense, tripMembers } = this.state;
    const existingIds = new Set((editingExpense?.splits || []).map((s) => s.memberId));
    return tripMembers.filter((m) => !existingIds.has(m.id)).map((m) => ({ id: m.id, label: m.name, name: m.name, value: m.id }));
  };

  onCategoryPicked = (items) => {
    this.setState({ pickerMode: null });
    if (items.length > 0) this.updateDraft("categoryId", items[0].value);
  };

  onLinkPicked = (items) => {
    this.setState({ pickerMode: null });
    if (items.length === 0) return;
    const value = items[0].value;
    if (value === "none") {
      this.setState((prev) => ({ editingExpense: { ...prev.editingExpense, placeId: null, eventKey: null } }));
      return;
    }
    const [type, id] = value.split(/:(.+)/);
    this.setState((prev) => ({
      editingExpense: {
        ...prev.editingExpense,
        placeId: type === "place" ? id : null,
        eventKey: type === "event" ? id : null,
      },
    }));
  };

  onPaidByPicked = (items) => {
    this.setState({ pickerMode: null });
    if (items.length > 0) this.updateDraft("paidBy", items[0].value);
  };

  onAddSplitMemberPicked = (items) => {
    this.setState({ pickerMode: null });
    if (items.length === 0) return;
    const memberId = items[0].value;
    this.setState((prev) => ({
      editingExpense: {
        ...prev.editingExpense,
        splits: [...prev.editingExpense.splits, { memberId, percent: 0 }],
      },
    }));
  };

  // ---- Lookups for display ----

  categoryName = (categoryId) => {
    const category = (window.guide_obj?.categories || []).find((c) => c.id === categoryId);
    return category ? category.name : "";
  };

  memberName = (memberId) => {
    const member = this.state.tripMembers.find((m) => m.id === memberId);
    return member ? member.name : "(removed)";
  };

  // Heading for an expense in the list (2026-10-09 — expenses had no title,
  // so a list of them was just amounts). Optional `title` field; without one,
  // falls back to the linked place/event, then the note's first line, then
  // the category, so older expenses still read sensibly. Returns which
  // source it used so it isn't repeated below. Mirrors mobile.
  expenseHeading = (expense) => {
    const title = (expense.title || "").trim();
    if (title) return { text: title, from: "title" };
    const link = this.linkChip(expense);
    if (link) return { text: link, from: "link" };
    const noteLine = (expense.note || "").split("\n")[0].trim();
    if (noteLine) return { text: noteLine, from: "note" };
    return { text: this.categoryName(expense.categoryId) || "Expense", from: "category" };
  };

  linkChip = (expense) => {
    if (expense.placeId) {
      const place = (window.guide_obj?.places?.placesList || []).find((p) => p.ID === expense.placeId);
      return place ? place.Name : null;
    }
    if (expense.eventKey) {
      const event = (window.guide_obj?.schedule?.events || []).find(
        (e) => (e.CommentKey || stableEventKey(e)) === expense.eventKey
      );
      return event ? event.Subject : null;
    }
    return null;
  };

  splitSummary = (expense) => {
    return (expense.splits || [])
      .map((s) => `${this.memberName(s.memberId)} ${s.percent}%`)
      .join(", ");
  };

  // ---- Save ----

  handleSave = () => {
    const { user_info } = this.props;
    window.guide_obj.tripMembers = this.state.tripMembers;
    window.guide_obj.expenses = this.state.expenses;
    if (user_info?.uid === "guest") window.guest_generated_unsaved_trip = true;
    this.props.dispatch(slice1Actions.setGuide({ guide: null }));
    this.setState({ dirty: false, saveMessage: "Saved to this guide — use \"Save Guide to Cloud\" to make it permanent." });
  };

  renderExpenseForm = () => {
    const { editingExpense, tripMembers } = this.state;
    const total = this.splitTotal();
    return (
      <div className="exs-form">
        <input
          className="exs-form-note"
          placeholder="Title (e.g. Hotel Brera)"
          maxLength={80}
          value={editingExpense.title || ""}
          onChange={(e) => this.updateDraft("title", e.target.value)}
        />
        <div className="exs-form-row">
          <input
            className="exs-form-amount"
            type="number"
            placeholder="Amount"
            value={editingExpense.amount}
            onChange={(e) => this.updateDraft("amount", e.target.value)}
          />
          <input
            className="exs-form-currency"
            placeholder="USD"
            maxLength={3}
            value={editingExpense.currency}
            onChange={(e) => this.updateDraft("currency", e.target.value.toUpperCase())}
          />
        </div>
        <button className="exs-form-picker-btn" onClick={this.openCategoryPicker}>
          Category: {this.categoryName(editingExpense.categoryId) || "Select…"}
        </button>
        <button className="exs-form-picker-btn" onClick={this.openLinkPicker}>
          Link: {editingExpense.placeId ? "Place selected" : editingExpense.eventKey ? "Event selected" : "None"}
        </button>
        <input
          className="exs-form-date"
          type="date"
          value={editingExpense.date}
          onChange={(e) => this.updateDraft("date", e.target.value)}
        />
        <button className="exs-form-picker-btn" onClick={this.openPaidByPicker} disabled={tripMembers.length === 0}>
          Paid by: {this.memberName(editingExpense.paidBy) || "Select…"}
        </button>
        <input
          className="exs-form-note"
          placeholder="Note (optional)"
          value={editingExpense.note}
          onChange={(e) => this.updateDraft("note", e.target.value)}
        />
        <div className="exs-split-label">
          Split — total: {total.toFixed(1)}%{Math.abs(total - 100) > 0.5 ? " (must be 100%)" : " ✓"}
        </div>
        {editingExpense.splits.map((s) => (
          <div className="exs-split-row" key={s.memberId}>
            <span className="exs-split-name">{this.memberName(s.memberId)}</span>
            <input
              className="exs-split-input"
              type="number"
              value={s.percent}
              onChange={(e) => this.updateSplitPercent(s.memberId, e.target.value)}
            />
            <span className="exs-split-pct">%</span>
          </div>
        ))}
        {this.addSplitMemberItems().length > 0 ? (
          <button className="exs-add-split-member-button" onClick={this.openAddSplitMemberPicker}>
            + Add Person to Split
          </button>
        ) : null}
        <div className="exs-form-buttons">
          <button className="exs-form-submit" onClick={this.saveExpenseDraft}>
            Save Expense
          </button>
          <button className="exs-form-cancel" onClick={this.cancelEditExpense}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  renderBalances = () => {
    const byCurrency = computeBalancesByCurrency(this.state.expenses, this.state.tripMembers);
    const currencies = Object.keys(byCurrency);
    if (currencies.length === 0) return null;
    return (
      <div className="exs-balances">
        <span className="exs-section-label">Balances</span>
        {currencies.map((currency) => (
          <div className="exs-card" key={currency}>
            <div className="exs-balance-currency">{currency}</div>
            {byCurrency[currency].map((row) => (
              <div className="exs-balance-row" key={row.memberId}>
                <span className="exs-balance-name">{row.name}</span>
                <span className={`exs-balance-net${row.net < 0 ? " exs-balance-negative" : row.net > 0 ? " exs-balance-positive" : ""}`}>
                  {row.net > 0 ? "+" : ""}
                  {row.net.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  render() {
    const { tripMembers, expenses, editingExpense, pickerMode, dirty, saveMessage, showTripMembersModal } = this.state;
    return (
      <>
        {showTripMembersModal ? <TripMembersModal show={true} onHide={this.closeTripMembersModal} /> : null}
        {pickerMode === "category" ? (
          <MultiSelectModal
            show={true}
            onHide={this.closePicker}
            MultiselectItems={this.categoryItems()}
            MultiselectSelectedItems={[]}
            MultiselectMode="uni"
            MultiselectCallback={this.onCategoryPicked}
            MultiselectTitle="Category"
            MultiselectSearchText=""
          />
        ) : null}
        {pickerMode === "link" ? (
          <MultiSelectModal
            show={true}
            onHide={this.closePicker}
            MultiselectItems={this.linkItems()}
            MultiselectSelectedItems={[]}
            MultiselectMode="uni"
            MultiselectCallback={this.onLinkPicked}
            MultiselectTitle="Link to a Place or Event"
            MultiselectSearchText=""
          />
        ) : null}
        {pickerMode === "paidBy" ? (
          <MultiSelectModal
            show={true}
            onHide={this.closePicker}
            MultiselectItems={this.paidByItems()}
            MultiselectSelectedItems={[]}
            MultiselectMode="uni"
            MultiselectCallback={this.onPaidByPicked}
            MultiselectTitle="Paid by"
            MultiselectSearchText=""
          />
        ) : null}
        {pickerMode === "addSplitMember" ? (
          <MultiSelectModal
            show={true}
            onHide={this.closePicker}
            MultiselectItems={this.addSplitMemberItems()}
            MultiselectSelectedItems={[]}
            MultiselectMode="uni"
            MultiselectCallback={this.onAddSplitMemberPicked}
            MultiselectTitle="Add Person to Split"
            MultiselectSearchText=""
          />
        ) : null}

        <span className="exs-section-label">Trip Members</span>
        <div className="exs-card">
          <div className="exs-members-row">
            {tripMembers.map((m) => (
              <span className="exs-member-chip" key={m.id}>
                {m.name}
              </span>
            ))}
            {tripMembers.length === 0 ? <span className="exs-empty">No trip members yet.</span> : null}
          </div>
          <button className="exs-new-expense-button" onClick={this.openTripMembersModal}>
            Manage Trip Members
          </button>
        </div>

        <span className="exs-section-label">Expenses</span>
        <div className="exs-card">
          {expenses.length === 0 && !editingExpense ? <div className="exs-empty">No expenses yet.</div> : null}
          {expenses.map((expense, index) =>
            editingExpense && this.state.editingExpenseIndex === index ? (
              <div key={expense.id}>{this.renderExpenseForm()}</div>
            ) : (
              <div className="exs-expense-row" key={expense.id}>
                <div className="exs-expense-head">
                  <span className="exs-expense-title">{this.expenseHeading(expense).text}</span>
                  <span className="exs-expense-amount">
                    {expense.amount} {expense.currency}
                  </span>
                  <div className="exs-expense-actions">
                    <FontAwesomeIcon icon={solid("pen")} className="exs-expense-action-icon" onClick={() => this.startEditExpense(expense, index)} />
                    <FontAwesomeIcon icon={solid("trash")} className="exs-expense-action-icon" onClick={() => this.deleteExpense(index)} />
                  </div>
                </div>
                <div className="exs-expense-meta">
                  {[
                    this.expenseHeading(expense).from !== "category" ? this.categoryName(expense.categoryId) : null,
                    this.expenseHeading(expense).from !== "link" ? this.linkChip(expense) : null,
                    expense.date,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                <div className="exs-expense-meta">
                  Paid by {this.memberName(expense.paidBy)} — {this.splitSummary(expense)}
                </div>
                {expense.note && this.expenseHeading(expense).from !== "note" ? (
                  <div className="exs-expense-note">{expense.note}</div>
                ) : null}
              </div>
            )
          )}
          {editingExpense && this.state.editingExpenseIndex === -1 ? this.renderExpenseForm() : null}
          {!editingExpense ? (
            <button className="exs-new-expense-button" onClick={this.startAddExpense} disabled={tripMembers.length === 0}>
              + Add Expense
            </button>
          ) : null}
          {tripMembers.length === 0 ? <div className="exs-hint">Add at least one trip member (above) before adding an expense.</div> : null}
        </div>

        {this.renderBalances()}

        <div className="exs-save-row">
          <button className="exs-save-button" onClick={this.handleSave} disabled={!dirty}>
            Save
          </button>
          {saveMessage ? <span className="exs-save-message">{saveMessage}</span> : null}
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  guide_info_id: state.guide_meta.guide_info_id,
  guide_info: state.guide_meta.guide_info,
  user_info: state.user_info,
});

export default connect(mapStateToProps)(ExpensesSection);
